import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, PieChart, CheckCircle, Truck, Tag, TrendingUp, ChevronRight, Target, Search, Star, BarChart4, Link as LinkIcon, Smartphone, Check, Medal, Zap, Phone } from "lucide-react";
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SEO from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import ContactForm from '@/components/ContactForm';
import CallToAction from '@/components/CallToAction';

const EcommerceSeo = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        seo={{
          title: "E-commerce SEO Services | Los Angeles SEO Group",
          description: "Specialized e-commerce SEO strategies to increase visibility, traffic and sales for your online store. Convert more visitors into customers.",
          canonical: "https://losangelesseogroup.com/services/ecommerce-seo",
        }}
        url="https://losangelesseogroup.com/services/ecommerce-seo"
      />
      <Navbar />
      
      <PageTransition>
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-indigo-50/50 to-white dark:from-indigo-950/20 dark:to-background">
            <div className="page-container">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div 
                  initial="hidden" 
                  animate="visible" 
                  variants={fadeIn}
                >
                  <div className="space-y-6">
                    <span className="inline-flex items-center rounded-full bg-indigo-100 dark:bg-indigo-900/30 px-3 py-1 text-sm font-medium text-indigo-800 dark:text-indigo-300">
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      E-commerce SEO
                    </span>
                    <h1 className="text-4xl md:text-5xl font-bold font-heading">
                      Turn Your Online Store Into a 
                      <span className="text-indigo-600 dark:text-indigo-400 block mt-2">
                        Sales-Generating Machine
                      </span>
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-xl">
                      Specialized SEO strategies for e-commerce businesses that drive qualified traffic, 
                      increase conversion rates, and grow your online store's revenue.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="lg" className="bg-indigo-gradient">
                            Get a Free E-commerce SEO Audit
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <div className="space-y-4">
                            <h3 className="text-xl font-bold">Request Your Free E-commerce SEO Audit</h3>
                            <p className="text-sm text-muted-foreground">
                              Fill out the form below and we'll analyze your online store and send you a detailed report.
                            </p>
                            <ContactForm onSuccess={() => setDialogOpen(false)} />
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <a 
                        href="tel:+13237841577" 
                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium transition-colors hover:bg-indigo-200 dark:hover:bg-indigo-900/50"
                      >
                        <Phone className="w-5 h-5" />
                        <span>(323) 784-1577</span>
                      </a>
                    </div>
                    
                    <div className="flex items-center gap-3 pt-3">
                      <div className="flex -space-x-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center border-2 border-background">
                            <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
                          </div>
                        ))}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Trusted by 100+ e-commerce businesses</span>
                        <span className="block text-muted-foreground">from startups to enterprise stores</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="lg:px-6"
                >
                  <div className="bg-card rounded-xl shadow-lg overflow-hidden border border-border">
                    <div className="p-6 space-y-5">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                            <PieChart className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold">E-commerce SEO Results</h3>
                            <p className="text-xs text-muted-foreground">Average client performance</p>
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
                          Verified
                        </span>
                      </div>
                      
                      <div className="space-y-4">
                        {[
                          { metric: "Organic Traffic Increase", value: "+147%", period: "first 6 months" },
                          { metric: "Conversion Rate Improvement", value: "+83%", period: "year over year" },
                          { metric: "Revenue Growth", value: "+215%", period: "from organic search" },
                          { metric: "Product Page Rankings", value: "Top 10", period: "for target keywords" }
                        ].map((stat, i) => (
                          <div key={i} className="flex justify-between items-center border-b border-border pb-3 last:border-0 last:pb-0">
                            <span className="text-sm">{stat.metric}</span>
                            <div className="text-right">
                              <span className="font-bold text-base text-green-600 dark:text-green-400">{stat.value}</span>
                              <span className="block text-xs text-muted-foreground">{stat.period}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="bg-muted p-4 border-t border-border">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Want these results for your store?</span>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="link" className="text-indigo-600 dark:text-indigo-400 gap-1 p-0">
                              Get started <ChevronRight className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <div className="space-y-4">
                              <h3 className="text-xl font-bold">Request a Free E-commerce SEO Analysis</h3>
                              <p className="text-sm text-muted-foreground">
                                Let us show you how we can improve your online store's performance.
                              </p>
                              <ContactForm />
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
          
          {/* Benefits Section */}
          <section className="py-16 md:py-24 bg-gradient-to-b from-white to-indigo-50/30 dark:from-background dark:to-indigo-950/10">
            <div className="page-container">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl md:text-4xl font-bold font-heading">
                  E-commerce SEO That Drives <span className="text-indigo-600 dark:text-indigo-400">Real Business Results</span>
                </h2>
                <p className="mt-4 text-muted-foreground text-lg">
                  Our specialized approach to e-commerce SEO focuses on revenue growth, not just rankings
                </p>
              </div>
              
              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {[
                  {
                    icon: <Search className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />,
                    title: "Product-focused SEO",
                    description: "Optimize individual product pages to rank for high-intent buying keywords and drive ready-to-purchase visitors."
                  },
                  {
                    icon: <Tag className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />,
                    title: "Category optimization",
                    description: "Structure and optimize category pages to capture broader search traffic and improve site architecture."
                  },
                  {
                    icon: <Smartphone className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />,
                    title: "Mobile-first approach",
                    description: "Ensure your store delivers an exceptional mobile experience for the majority of today's shoppers."
                  },
                  {
                    icon: <BarChart4 className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />,
                    title: "Conversion optimization",
                    description: "Improve product page elements that drive higher conversion rates and average order values."
                  },
                  {
                    icon: <Truck className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />,
                    title: "Technical performance",
                    description: "Enhance site speed, structure and crawlability to improve search rankings and user experience."
                  },
                  {
                    icon: <LinkIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />,
                    title: "Authority building",
                    description: "Earn high-quality backlinks from relevant industry sites to boost domain authority and rankings."
                  }
                ].map((benefit, i) => (
                  <motion.div 
                    key={i}
                    variants={fadeIn}
                    className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-5">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
          
          {/* Service Details Section */}
          <section className="py-16 md:py-24">
            <div className="page-container">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <span className="inline-flex items-center rounded-full bg-indigo-100 dark:bg-indigo-900/30 px-3 py-1 text-sm font-medium text-indigo-800 dark:text-indigo-300 mb-6">
                    <Zap className="w-4 h-4 mr-1" />
                    Our Process
                  </span>
                  
                  <h2 className="text-3xl font-bold font-heading mb-6">
                    Comprehensive E-commerce <span className="text-indigo-600 dark:text-indigo-400">SEO Strategy</span>
                  </h2>
                  
                  <p className="text-muted-foreground mb-8">
                    Our specialized approach to e-commerce SEO addresses the unique challenges online stores face in a competitive marketplace.
                  </p>
                  
                  <Tabs defaultValue="analysis" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="analysis">Analysis</TabsTrigger>
                      <TabsTrigger value="strategy">Strategy</TabsTrigger>
                      <TabsTrigger value="execution">Execution</TabsTrigger>
                    </TabsList>
                    <TabsContent value="analysis" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Comprehensive Store Audit</p>
                            <p>Complete technical analysis of your store architecture, product structure, and content organization</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Competitor Research</p>
                            <p>In-depth analysis of top-performing competitors to identify opportunities and gaps</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Keyword &amp; Market Research</p>
                            <p>Product-focused keyword research to identify high-converting search terms</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">UX &amp; Conversion Analysis</p>
                            <p>Evaluation of user experience and conversion bottlenecks throughout the purchase funnel</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="strategy" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Product Page Optimization</p>
                            <p>Strategic enhancement of product titles, descriptions, and technical elements for maximum visibility</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Category Structure Planning</p>
                            <p>Optimized information architecture to improve crawlability and user navigation</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Content Strategy Development</p>
                            <p>Creation of educational content to support product pages and build topical authority</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Technical SEO Roadmap</p>
                            <p>Prioritized plan to address technical issues impacting performance and crawlability</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="execution" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Advanced Schema Implementation</p>
                            <p>Product schema markup for rich snippets and enhanced SERP visibility</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Content Creation &amp; Optimization</p>
                            <p>Development of product descriptions, category content, and educational resources</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Technical Implementation</p>
                            <p>Execution of technical fixes for site speed, mobile responsiveness, and crawlability</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Authority Building</p>
                            <p>Strategic link acquisition from industry publications and relevant websites</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                  
                  <div className="mt-8">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="lg" className="bg-indigo-gradient">
                          Get a Customized Strategy
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <div className="space-y-4">
                          <h3 className="text-xl font-bold">Request Your Custom E-commerce SEO Strategy</h3>
                          <p className="text-sm text-muted-foreground">
                            Let our experts analyze your store and create a tailored plan for growth.
                          </p>
                          <ContactForm />
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                
                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 p-1 rounded-2xl shadow-xl">
                    <div className="bg-card rounded-xl p-8 border border-border">
                      <div className="space-y-6">
                        <div className="flex gap-4 items-start">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                            <Medal className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold">E-commerce SEO Package</h3>
                            <p className="text-sm text-muted-foreground">Comprehensive optimization for online stores</p>
                          </div>
                        </div>
                        
                        <div className="pt-2">
                          <div className="text-3xl font-bold">$3,500<span className="text-lg font-normal text-muted-foreground">/month</span></div>
                          <p className="text-sm text-muted-foreground mt-1">Custom pricing based on store size and competition</p>
                        </div>
                        
                        <div className="space-y-3 pt-3">
                          {[
                            "Product page optimization",
                            "Category page optimization",
                            "Schema markup implementation",
                            "Shopping feed optimization",
                            "Conversion rate optimization",
                            "Marketplace integration",
                            "Advanced analytics tracking"
                          ].map((feature, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-4 space-y-3">
                          <Button className="w-full bg-indigo-gradient" size="lg" asChild>
                            <Link to="/contact">Start Your Campaign</Link>
                          </Button>
                          
                          <div className="text-center">
                            <a 
                              href="tel:+13237841577" 
                              className="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:underline flex items-center justify-center gap-1"
                            >
                              <Phone className="w-4 h-4" />
                              Call for custom pricing: (323) 784-1577
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -z-10 -bottom-6 -right-6 w-64 h-64 bg-indigo-200/50 dark:bg-indigo-900/10 rounded-full blur-3xl"></div>
                  <div className="absolute -z-10 -top-6 -left-6 w-48 h-48 bg-purple-200/50 dark:bg-purple-900/10 rounded-full blur-3xl"></div>
                </motion.div>
              </div>
            </div>
          </section>
          
          {/* Case Studies Section */}
          <section className="py-16 md:py-24 bg-muted/50">
            <div className="page-container">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-3xl font-bold font-heading">
                  Real Results for <span className="text-indigo-600 dark:text-indigo-400">E-commerce Businesses</span>
                </h2>
                <p className="mt-4 text-muted-foreground">
                  See how we've helped online stores increase traffic, conversions, and revenue
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    title: "Fashion Retailer",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "Organic Traffic", value: "+213%" },
                      { label: "Conversion Rate", value: "+68%" },
                      { label: "Revenue Growth", value: "+178%" }
                    ],
                    summary: "Complete optimization of product taxonomy, content strategy, and technical structure.",
                    link: "/case-studies"
                  },
                  {
                    title: "Home Goods Store",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "Keywords Ranking", value: "+340%" },
                      { label: "Page Views", value: "+156%" },
                      { label: "Transactions", value: "+89%" }
                    ],
                    summary: "Product-focused SEO strategy with enhanced schema implementation and content.",
                    link: "/case-studies"
                  },
                  {
                    title: "Beauty Products",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "Organic Sessions", value: "+147%" },
                      { label: "Avg Order Value", value: "+23%" },
                      { label: "Return Visitors", value: "+82%" }
                    ],
                    summary: "Category restructuring and product page optimization focused on high-value terms.",
                    link: "/case-studies"
                  }
                ].map((study, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-card border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-video bg-muted relative">
                      <img 
                        src={study.image} 
                        alt={study.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                        <h3 className="text-white font-bold text-xl p-4">{study.title}</h3>
                      </div>
                    </div>
                    
                    <div className="p-5">
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {study.stats.map((stat, j) => (
                          <div key={j} className="text-center">
                            <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{stat.value}</div>
                            <div className="text-xs text-muted-foreground">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-4">
                        {study.summary}
                      </p>
                      
                      <Link 
                        to={study.link}
                        className="flex items-center text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                      >
                        Read Case Study <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button asChild variant="outline" className="gap-1">
                  <Link to="/case-studies">
                    View All Case Studies <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>
          
          {/* FAQ Section */}
          <section className="py-16 md:py-24">
            <div className="page-container max-w-4xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold font-heading">
                  Frequently Asked Questions About <span className="text-indigo-600 dark:text-indigo-400">E-commerce SEO</span>
                </h2>
              </div>
              
              <div className="space-y-6">
                {[
                  {
                    q: "How long does it take to see results from e-commerce SEO?",
                    a: "Most e-commerce clients begin seeing initial improvements within 2-3 months, with significant results by month 4-6. The timeline depends on your store's current authority, technical foundation, and competition level. Our strategies focus on early wins while building long-term growth."
                  },
                  {
                    q: "How do you approach product pages that have little to no search volume?",
                    a: "For products with low search volumes, we implement a clustering strategy that groups related products under optimized category pages that target broader terms. We also create supporting content to build topical relevance and identify long-tail opportunities that can drive qualified traffic."
                  },
                  {
                    q: "Do you work with specific e-commerce platforms?",
                    a: "We work with all major e-commerce platforms including Shopify, WooCommerce, Magento, BigCommerce, and custom solutions. Each platform has unique SEO considerations, and our team includes specialists familiar with the technical requirements of each system."
                  },
                  {
                    q: "How do you handle products that go out of stock or are discontinued?",
                    a: "We implement strategic redirection and content preservation tactics to maintain SEO value. For temporary out-of-stock items, we use proper schema markup and messaging. For discontinued products, we create a plan to redirect equity to similar items or categories to preserve rankings."
                  },
                  {
                    q: "Can you help with marketplace optimization (Amazon, Etsy, etc.)?",
                    a: "Yes, our e-commerce SEO services include marketplace optimization strategies. We help you maximize visibility across all sales channels while ensuring your direct website benefits from a comprehensive approach that builds your brand's long-term authority."
                  }
                ].map((faq, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true, margin: "-50px" }}
                    className="bg-card border border-border rounded-lg p-6 shadow-sm"
                  >
                    <h3 className="text-lg font-semibold mb-3">{faq.q}</h3>
                    <p className="text-muted-foreground text-sm">{faq.a}</p>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-10 text-center">
                <p className="text-muted-foreground mb-4">Still have questions about our e-commerce SEO services?</p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <a 
                    href="tel:+13237841577" 
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium transition-colors hover:bg-indigo-200 dark:hover:bg-indigo-900/50"
                  >
                    <Phone className="w-5 h-5" />
                    <span>(323) 784-1577</span>
                  </a>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button>Contact Us Online</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <div className="space-y-4">
                        <h3 className="text-xl font-bold">Have Questions?</h3>
                        <p className="text-sm text-muted-foreground">
                          Fill out the form below and our e-commerce SEO specialist will get back to you.
                        </p>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </section>
          
          {/* CTA Section */}
          <section className="py-16 md:py-20 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30">
            <div className="page-container">
              <div className="max-w-4xl mx-auto bg-card rounded-2xl border border-border overflow-hidden shadow-lg">
                <div className="p-8 md:p-10">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold font-heading">
                      Ready to Transform Your <span className="text-indigo-600 dark:text-indigo-400">E-commerce Store</span>?
                    </h2>
                    <p className="mt-3 text-muted-foreground">
                      Get a free consultation and discover how our e-commerce SEO services can grow your online sales
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                    <div className="md:col-span-2">
                      <ContactForm />
                    </div>
                    
                    <div className="bg-muted/60 p-6 rounded-xl border border-border/50">
                      <h3 className="font-semibold mb-3">Or Call Us Directly</h3>
                      <a 
                        href="tel:+13237841577" 
                        className="flex items-center gap-2 px-5 py-3 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium transition-colors hover:bg-indigo-200 dark:hover:bg-indigo-900/50 w-full justify-center"
                      >
                        <Phone className="w-5 h-5" />
                        <span>(323) 784-1577</span>
                      </a>
                      <p className="text-xs text-muted-foreground mt-3 text-center">
                        Speak directly with an e-commerce SEO specialist who can answer your questions
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          <CallToAction />
        </main>
      </PageTransition>
      
      <Footer />
    </div>
  );
};

export default EcommerceSeo;
